import React, { useState, useEffect } from 'react'
import FyMonthlyBarChart from '../../components/Charts/FyMonthlyBarChart'
import { currentFyLabel } from '../../utils/financialYearMonths'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import contractsDashboardService from '../../services/contractsDashboardService'
import { formatCurrency } from '../../utils/currencyFormat'
import { useModuleView } from '../../hooks/useModuleView'
import ModuleOverviewCard from '../../components/Module/ModuleOverviewCard'
import ModuleViewBackLink from '../../components/Module/ModuleViewBackLink'
import { 
  Users,
  Activity,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Brain,
  DollarSign,
  Target,
  AlertTriangle,
  CheckCircle,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  MessageSquare,
  FileText,
  Briefcase,
  FileSignature,
  Shield,
  Scale,
  Archive
} from 'lucide-react'
// Consolidated CSS is imported globally'

const Contracts = () => {
  const navigate = useNavigate()
  const moduleView = useModuleView()
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const response = await contractsDashboardService.getDashboardStats()
      if (response.success) {
        setDashboardData(response.data)
      } else {
        if (!response._errorHandled) {
          toast.error(response.message || 'Failed to load dashboard data')
        }
      }
    } catch (error) {
      console.error('Error loading contracts dashboard data:', error)
      if (!error._errorHandled) {
        if (error.response) {
          toast.error(error.response.data?.message || 'Failed to load dashboard data')
        } else if (!error.request) {
          toast.error('Failed to load dashboard data')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatCurrencyDisplay = (amount, currency = 'INR') => formatCurrency(amount, currency, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Just now'
    const date = new Date(timestamp)
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)
    
    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="sales-dashboard">
      {/* Page Header */}
      <div className="page-header" style={{textAlign: 'left'}}>
        <div className="page-title" style={{textAlign: 'left', alignItems: 'flex-start'}}>
          <h1 style={{textAlign: 'left', margin: '0 0 4px 0'}}>Contracts</h1>
          <p style={{textAlign: 'left', margin: 0}}>Manage contracts, agreements, and legal documentation</p>
        </div>
      </div>

      {/* Tab Content */}
      {moduleView === 'overview' && (
        <div className="tab-content">
          <ModuleViewBackLink onClick={() => navigate('/contracts')} />

          {/* Key Metrics Grid */}
          <div className="dashboard-metrics-grid">
            {/* Contract Metrics */}
            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon">
                  <FileSignature size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.activeContracts?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.activeContracts?.change >= 0 ? '+' : ''}{dashboardData?.activeContracts?.change || 0}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : (dashboardData?.activeContracts?.count || 0)}
              </div>
              <div className="metric-title">Active Contracts</div>
              <div className="metric-subtitle">vs last month</div>
            </div>

            <div className="metric-card customers">
              <div className="metric-header">
                <div className="metric-icon">
                  <DollarSign size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.contractValue?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.contractValue?.change >= 0 ? '+' : ''}{dashboardData?.contractValue?.change?.toFixed(1) || 0}%</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : formatCurrencyDisplay(dashboardData?.contractValue?.value || 0, dashboardData?.contractValue?.currency || 'INR')}
              </div>
              <div className="metric-title">Contract Value</div>
              <div className="metric-subtitle">Total value</div>
            </div>

            <div className="metric-card churn">
              <div className="metric-header">
                <div className="metric-icon">
                  <AlertTriangle size={16} />
                </div>
                <div className="metric-trend negative">
                  <TrendingDown size={12} />
                  <span>Next {dashboardData?.expiringSoon?.days || 30} days</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : (dashboardData?.expiringSoon?.count || 0)}
              </div>
              <div className="metric-title">Expiring Soon</div>
              <div className="metric-subtitle">Requires attention</div>
            </div>

            <div className="metric-card burn">
              <div className="metric-header">
                <div className="metric-icon">
                  <Shield size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.complianceRate?.status === 'NO_DATA' ? 'neutral' : dashboardData?.complianceRate?.status === 'EXCELLENT' || dashboardData?.complianceRate?.status === 'GOOD' ? 'positive' : 'negative'}`}>
                  <CheckCircle size={12} />
                  <span>{(dashboardData?.complianceRate?.status || 'NO_DATA').replace(/_/g, ' ')}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading
                  ? '...'
                  : dashboardData?.complianceRate?.status === 'NO_DATA'
                    ? '—'
                    : `${dashboardData?.complianceRate?.rate || 0}%`}
              </div>
              <div className="metric-title">Compliance Rate</div>
              <div className="metric-subtitle">Regulatory compliance</div>
            </div>
          </div>

          {/* Action Items & Alerts */}
          <div className="dashboard-action-section">
            <div className="dashboard-action-items">
              <div className="section-header">
                <h3>Contract Action Items</h3>
                <span className="item-count">{dashboardData?.actionItems?.length || 0} items</span>
              </div>
              <div className="action-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading action items...</div>
                ) : dashboardData?.actionItems && dashboardData.actionItems.length > 0 ? (
                  dashboardData.actionItems.map((item, index) => {
                    const iconMap = {
                      renewal: AlertTriangle,
                      review: FileText,
                      template: Scale
                    }
                    const Icon = iconMap[item.type] || AlertTriangle
                    return (
                      <div key={index} className={`action-item ${item.priority}`}>
                        <div className="action-icon">
                          <Icon size={16} />
                        </div>
                        <div className="action-content">
                          <div className="action-title">{item.title}</div>
                          <div className="action-description">{item.description}</div>
                        </div>
                        <div className="action-time">{formatTimeAgo(item.timestamp)}</div>
                      </div>
                    )
                  })
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No action items</div>
                )}
              </div>
            </div>

            <div className="dashboard-team-activity">
              <div className="section-header">
                <h3>Contract Team Activity</h3>
                <span className="activity-count">{dashboardData?.teamActivity?.length || 0} updates</span>
              </div>
              <div className="activity-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading team activity...</div>
                ) : dashboardData?.teamActivity && dashboardData.teamActivity.length > 0 ? (
                  dashboardData.teamActivity.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-avatar">{activity.employeeInitials || 'UN'}</div>
                      <div className="activity-content">
                        <div className="activity-text">
                          <strong>{activity.employeeName || 'Unknown'}</strong> {activity.activity}
                        </div>
                        <div className="activity-time">{formatTimeAgo(activity.timestamp)}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No recent activity</div>
                )}
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="dashboard-charts-section">
            <div className="dashboard-chart-card revenue-chart">
              <div className="chart-header">
                <h3>Contract Value Trend</h3>
                <p>{dashboardData?.financialYear ? `FY ${dashboardData.financialYear}` : `FY ${currentFyLabel()}`}</p>
              </div>
              <div className="chart-content">
                <FyMonthlyBarChart
                  series={dashboardData?.contractValueTrend}
                  valueKey="revenue"
                  formatTooltip={(v) => formatCurrencyDisplay(v, 'INR')}
                  emptyMessage="No signed contracts this financial year"
                />
              </div>
            </div>

            <div className="dashboard-chart-card customer-health">
              <div className="chart-header">
                <h3>Contract Status Distribution</h3>
                <p>Current portfolio</p>
              </div>
              <div className="chart-content">
                <div className="health-distribution">
                  {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading...</div>
                  ) : dashboardData?.statusDistribution?.length > 0 ? (
                    dashboardData.statusDistribution.map((row, index) => {
                      const classes = ['excellent', 'good', 'at-risk', 'churned']
                      const cls = classes[index % classes.length]
                      return (
                        <div key={row.name || index} className={`health-segment ${cls}`}>
                          <div className="segment-bar" style={{ width: `${Math.max(row.percent || 0, row.count > 0 ? 4 : 0)}%` }} />
                          <div className="segment-info">
                            <span className="segment-label">{row.name}</span>
                            <span className="segment-count">{row.count ?? 0}</span>
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                      No contracts in portfolio
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'masters' && (
        <div className="tab-content">
          <div className="contracts-masters-content">
            <div className="contracts-masters-header">
              <h2>Contract Masters</h2>
              <p>Manage your contract master data and configurations</p>
            </div>
            <div className="contracts-masters-grid">
              <div className="contracts-master-card clickable" onClick={() => navigate('/contracts/contract-type-master')}>
                <div className="contracts-master-icon">
                  <FileSignature size={24} />
                </div>
                <div className="contracts-master-content">
                  <h3>Contract Types</h3>
                  <p>Define different types of contracts and agreements</p>
                  <div className="contracts-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.contractTypes ?? 0} Types`}
                  </div>
                </div>
              </div>
              <div className="contracts-master-card clickable" onClick={() => navigate('/contracts/legal-terms-master')}>
                <div className="contracts-master-icon">
                  <Scale size={24} />
                </div>
                <div className="contracts-master-content">
                  <h3>Legal Terms</h3>
                  <p>Standard legal terms and conditions templates</p>
                  <div className="contracts-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.legalTerms ?? 0} Templates`}
                  </div>
                </div>
              </div>
              <div className="contracts-master-card clickable" onClick={() => navigate('/contracts/approval-workflow-master')}>
                <div className="contracts-master-icon">
                  <Users size={24} />
                </div>
                <div className="contracts-master-content">
                  <h3>Approval Workflows</h3>
                  <p>Contract approval processes and signatories</p>
                  <div className="contracts-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.approvalWorkflows ?? 0} Workflows`}
                  </div>
                </div>
              </div>
              <div className="contracts-master-card clickable" onClick={() => navigate('/contracts/compliance-rule-master')}>
                <div className="contracts-master-icon">
                  <Shield size={24} />
                </div>
                <div className="contracts-master-content">
                  <h3>Compliance Rules</h3>
                  <p>Regulatory compliance and audit requirements</p>
                  <div className="contracts-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.complianceRules ?? 0} Rules`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'transactions' && (
        <div className="tab-content">
          <div className="contracts-transactions-content">
            <div className="contracts-transactions-header">
              <h2>Contract Management</h2>
              <p>Track and manage all contract lifecycle activities</p>
            </div>
            <div className="contracts-transactions-grid">
              <ModuleOverviewCard
                prefix="contracts-transaction"
                moduleName="Contracts"
                showArrow={false}
                onClick={() => navigate('/contracts?view=overview')}
              />
              <div className="contracts-transaction-card clickable" onClick={() => navigate('/contracts/draft-contract')}>
                <div className="contracts-transaction-icon">
                  <FileText size={24} />
                </div>
                <div className="contracts-transaction-content">
                  <h3>Draft Contracts</h3>
                  <p>Create and manage contract drafts</p>
                  <div className="contracts-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.drafts ?? 0} Drafts`}
                  </div>
                </div>
              </div>
              <div className="contracts-transaction-card clickable" onClick={() => navigate('/contracts/signed-contract')}>
                <div className="contracts-transaction-icon">
                  <FileSignature size={24} />
                </div>
                <div className="contracts-transaction-content">
                  <h3>Signed Contracts</h3>
                  <p>Executed and active contracts</p>
                  <div className="contracts-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.activeSigned ?? 0} Active`}
                  </div>
                </div>
              </div>
              <div className="contracts-transaction-card clickable" onClick={() => navigate('/contracts/contract-renewal')}>
                <div className="contracts-transaction-icon">
                  <Calendar size={24} />
                </div>
                <div className="contracts-transaction-content">
                  <h3>Renewals</h3>
                  <p>Contract renewal and extension management</p>
                  <div className="contracts-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.renewalsDue ?? 0} Due`}
                  </div>
                </div>
              </div>
              <div className="contracts-transaction-card clickable" onClick={() => navigate('/contracts/archived-contract')}>
                <div className="contracts-transaction-icon">
                  <Archive size={24} />
                </div>
                <div className="contracts-transaction-content">
                  <h3>Archived</h3>
                  <p>Completed and archived contracts</p>
                  <div className="contracts-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.archived ?? 0} Archived`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'reports' && (
        <div className="tab-content">
          <div className="contracts-reports-content">
            <div className="contracts-reports-header">
              <h2>Contract Reports</h2>
              <p>Comprehensive contract analytics and reporting</p>
            </div>
            <div className="contracts-reports-grid">
              {[
                { path: '/contracts/reports/performance', icon: BarChart3, title: 'Contract Performance', description: 'Contract value and performance analysis' },
                { path: '/contracts/reports/renewals', icon: Calendar, title: 'Renewal Reports', description: 'Contract renewal and expiration tracking' },
                { path: '/contracts/reports/compliance', icon: Shield, title: 'Compliance Reports', description: 'Regulatory compliance and audit reports' },
                { path: '/contracts/reports/financial', icon: TrendingUp, title: 'Financial Reports', description: 'Contract financial impact and revenue analysis' }
              ].map(({ path, icon: Icon, title, description }) => (
                <div
                  key={path}
                  className="contracts-report-card clickable"
                  style={{ cursor: 'pointer' }}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(path)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate(path)}
                >
                  <div className="contracts-report-icon">
                    <Icon size={24} />
                  </div>
                  <div className="contracts-report-content">
                    <h3>{title}</h3>
                    <p>{description}</p>
                    <div className="contracts-report-status">Live data</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Contracts




